function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, canonicalize(item)]),
    )
  }
  return value
}

function fingerprint(value: unknown): string {
  return JSON.stringify(canonicalize(value))
}

function randomKey(prefix: string): string {
  const uuid = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `${prefix}:${uuid}`
}

export interface IdempotencyAttempt {
  keyFor(payload: unknown): string
  reset(): void
}

/**
 * Keeps the same command key while a user retries the exact same payload after
 * a timeout/network failure. Editing the form changes the payload fingerprint
 * and automatically starts a new command key.
 */
export function createIdempotencyAttempt(prefix: string): IdempotencyAttempt {
  let lastFingerprint = ''
  let currentKey = ''

  return {
    keyFor(payload: unknown): string {
      const nextFingerprint = fingerprint(payload)
      if (currentKey === '' || lastFingerprint !== nextFingerprint) {
        currentKey = randomKey(prefix)
        lastFingerprint = nextFingerprint
      }
      return currentKey
    },
    reset(): void {
      lastFingerprint = ''
      currentKey = ''
    },
  }
}
